import { useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button, Card, Input } from "../../../../../shared";
import { CardHeader } from "../../../../../shared/Card/CardHeader";
import { useCreateAdminCustomerBillingInvoiceMutation } from "../../../../../redux/services/admin/adminApi";

const toInputDate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const buildDefaultPeriod = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const due = new Date(now.getFullYear(), now.getMonth(), 15);

  return {
    billingPeriodStart: toInputDate(start),
    billingPeriodEnd: toInputDate(end),
    dueDate: toInputDate(due),
  };
};

/**
 * Super-admin form to create manual subscription invoices for contract tenants.
 */
export const ManualBillingInvoiceForm = ({ customerId, onCreated }) => {
  const defaults = useMemo(() => buildDefaultPeriod(), []);
  const [form, setForm] = useState({
    description: "AttuneLogic subscription",
    amount: "",
    paymentUrl: "",
    ...defaults,
  });
  const [createInvoice, { isLoading }] = useCreateAdminCustomerBillingInvoiceMutation();

  const updateField = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!customerId) {
      return;
    }

    try {
      const response = await createInvoice({
        id: customerId,
        description: form.description,
        amount: Number(form.amount),
        billingPeriodStart: form.billingPeriodStart,
        billingPeriodEnd: form.billingPeriodEnd,
        dueDate: form.dueDate,
        paymentUrl: form.paymentUrl?.trim() || undefined,
      }).unwrap();

      if (response?.notification?.sent) {
        toast.success(
          response?.invoice?.paymentUrl
            ? "Invoice created and payment email sent with pay link."
            : "Invoice created and payment reminder email sent."
        );
      } else if (response?.notification?.reason === "due_date_required") {
        toast.warn(
          "Invoice created, but no email was sent because a due date is required."
        );
      } else if (response?.notification?.reason === "email_failed") {
        toast.warn("Invoice created, but the payment reminder email failed to send.");
      } else {
        toast.success("Invoice created.");
      }
      setForm({
        description: "AttuneLogic subscription",
        amount: "",
        paymentUrl: "",
        ...buildDefaultPeriod(),
      });
      onCreated?.();
    } catch (error) {
      console.error("Failed to create manual billing invoice:", error);
      toast.error(error?.data?.message || "Could not create invoice. Please try again.");
    }
  };

  return (
    <Card
      header={<CardHeader title="Create manual invoice" />}
      content={
        <form className="grid grid-cols-1 gap-3 md:grid-cols-2" onSubmit={handleSubmit}>
          <Input
            label="Description"
            value={form.description}
            onChange={(target) => updateField("description", target.value)}
          />
          <Input
            label="Amount (USD)"
            type="number"
            min="0"
            step="0.01"
            required
            value={form.amount}
            onChange={(target) => updateField("amount", target.value)}
          />
          <Input
            label="Billing period start"
            type="date"
            required
            value={form.billingPeriodStart}
            onChange={(target) => updateField("billingPeriodStart", target.value)}
          />
          <Input
            label="Billing period end"
            type="date"
            required
            value={form.billingPeriodEnd}
            onChange={(target) => updateField("billingPeriodEnd", target.value)}
          />
          <Input
            label="Due date"
            type="date"
            required
            value={form.dueDate}
            onChange={(target) => updateField("dueDate", target.value)}
          />
          <div className="md:col-span-2">
            <Input
              label="Payment link (optional)"
              type="url"
              placeholder="https://pay.stripe.com/..."
              value={form.paymentUrl}
              onChange={(target) => updateField("paymentUrl", target.value)}
            />
          </div>
          <p className="md:col-span-2 text-xs text-gray-500">
            A payment reminder email is sent when the invoice is created. If you add a payment
            link, it is included in the email and invoice details for the customer to pay online.
          </p>
          <div className="md:col-span-2 flex justify-end">
            <Button
              htmlType="submit"
              title={isLoading ? "Creating..." : "Create invoice"}
              disabled={isLoading}
              loading={isLoading}
            />
          </div>
        </form>
      }
    />
  );
};
